import { useCallback, useEffect, useRef, useState } from 'react';
import { createWebSocket, type WsConnection } from '../api/websocket';
import type { KnowledgeGraph } from '../api/knowledge';
import { GraphView } from '../components/graph/GraphView';
import './StagePage.css';
import { useBrand, useInstanceName } from '../InstanceName';

type StageFrame =
  | { type: 'html'; title: string; content: string; timestamp?: number }
  | { type: 'markdown'; title: string; content: string; timestamp?: number }
  | { type: 'graph'; title: string; data: KnowledgeGraph; timestamp?: number };

let frameId = 0;

interface HistoryItem {
  id: number;
  frame: StageFrame;
  receivedAt: number;
}

export function StagePage() {
  const instanceName = useInstanceName();
  const [current, setCurrent] = useState<HistoryItem | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [connected, setConnected] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const wsRef = useRef<WsConnection | null>(null);

  const handleFrame = useCallback((frame: StageFrame) => {
    const item: HistoryItem = {
      id: ++frameId,
      frame,
      receivedAt: Date.now(),
    };
    setCurrent(item);
    setHistory((prev) => [item, ...prev].slice(0, 50));
  }, []);

  useEffect(() => {
    const ws = createWebSocket('/ws/stage', {
      onOpen: () => setConnected(true),
      onClose: () => setConnected(false),
      onMessage: (data) => {
        if (isStageFrame(data)) handleFrame(data);
      },
    });
    wsRef.current = ws;
    return () => ws.close();
  }, [handleFrame]);

  return (
    <div className="stage-page">
      <header className="stage-page__header">
        <div>
          <div className="stage-page__title">Stage</div>
          <div className="stage-page__subtitle">
            Agent-pushed presentations · {history.length} in session
          </div>
        </div>
        <div className="stage-page__actions">
          <div className={`stage-page__status${connected ? ' stage-page__status--ok' : ''}`}>
            <span className="stage-page__dot" />
            {connected ? 'live' : 'connecting...'}
          </div>
          <button
            type="button"
            className="stage-page__history-btn"
            onClick={() => setDrawerOpen((v) => !v)}
          >
            History ({history.length})
          </button>
        </div>
      </header>

      <div className="stage-page__body">
        {current ? (
          <StageContent item={current} />
        ) : (
          <div className="stage-page__empty">
            <div className="stage-page__empty-title">No active presentation</div>
            <div className="stage-page__empty-subtitle">
              The stage appears when {instanceName} pushes content.
            </div>
          </div>
        )}
      </div>

      {drawerOpen && (
        <aside className="stage-page__drawer">
          <header className="stage-page__drawer-head">
            <span>History</span>
            <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Close">
              ×
            </button>
          </header>
          <ul className="stage-page__history">
            {history.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`stage-page__history-item${
                    current?.id === item.id ? ' stage-page__history-item--active' : ''
                  }`}
                  onClick={() => setCurrent(item)}
                >
                  <span className="stage-page__history-kind">{item.frame.type}</span>
                  <span className="stage-page__history-title">{item.frame.title}</span>
                  <span className="stage-page__history-time">
                    {formatTime(item.receivedAt)}
                  </span>
                </button>
              </li>
            ))}
            {history.length === 0 && (
              <li className="stage-page__history-empty">No pushes yet.</li>
            )}
          </ul>
        </aside>
      )}
    </div>
  );
}

function isStageFrame(data: unknown): data is StageFrame {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (typeof d.type !== 'string') return false;
  return d.type === 'html' || d.type === 'markdown' || d.type === 'graph';
}

function formatTime(ms: number): string {
  const date = new Date(ms);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function StageContent({ item }: { item: HistoryItem }) {
  const { frame } = item;

  return (
    <div className="stage-content glass">
      <header className="stage-content__head">
        <div className="label">{frame.type}</div>
        <div className="stage-content__title">{frame.title}</div>
      </header>
      <div className="stage-content__body">
        {frame.type === 'markdown' && <MarkdownView content={frame.content} />}
        {frame.type === 'html' && <HtmlView content={frame.content} />}
        {frame.type === 'graph' && (
          <div className="stage-content__graph">
            <GraphView graph={frame.data} />
          </div>
        )}
      </div>
    </div>
  );
}

function MarkdownView({ content }: { content: string }) {
  return <pre className="stage-md">{content}</pre>;
}

function HtmlView({ content }: { content: string }) {
  // A sandboxed document cannot read the page's CSS variables, so the
  // brand's secondary accent is written into its stylesheet directly. It is
  // `#rrggbb` by construction — validated on the server and again in
  // `api/instance.ts` — so interpolating it here is safe.
  const linkColour = useBrand().accentSecondary;
  return (
    <iframe
      className="stage-iframe"
      sandbox=""
      srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>
body{background:transparent;color:rgba(255,255,255,0.85);font-family:system-ui,sans-serif;padding:16px;margin:0}
a{color:${linkColour}}
</style></head><body>${content}</body></html>`}
    />
  );
}
